<template>
	<label :for="`input-${name}`" class="bbox-text-input-label" :class="{ 'has-error': !!error }">
		<span :class="[labelClassesToApply, 'label']" v-if="!$slots.label && !!label">{{ label }}</span>
		<slot name="label" />
		<div class="input-wrapper" :class="{ autosize }">
			<span class="icon-left" v-if="$slots['icon-left']"><slot name="icon-left"/></span>
			<span class="icon-right" v-if="$slots['icon-right']"><slot name="icon-right"/></span>
			<div class="display-flex">
				<input
					ref="input"
					class="bbox-text-input"
					:autocomplete="noAutocomplete ? 'off' : null"
					:class="[
						inputClassesToApply,
						{ 'has-icon-left': !!$slots['icon-left'], 'has-icon-right': !!$slots['icon-right'] }
					]"
					:type="type"
					:id="`input-${name}`"
					:value="value"
					:name="name"
					@change="
						$event => {
							$emit('change', $event.target.value);
						}
					"
					@input="
						$event => {
							$emit('input', $event.target.value);
							updateSize();
						}
					"
					:placeholder="placeholder"
					:maxlength="maxlength"
					:disabled="disabled"
					v-on="datePickerEvent"
				/>
				<slot name="sideLabel" />
			</div>
		</div>
		<span class="input-error font-size-big red-base margin-top-tiny" v-if="!!error"
			><b-icon><icon-error /></b-icon> {{ error }}</span
		>
		<p
			v-if="counter"
			class="margin-top-tiny margin-bottom-0 regular align-right font-size-normal ink-lighter-1"
		>
			{{ maxlength - value.length }} caracteres restantes
		</p>
	</label>
</template>

<script>
import BIcon from './Icon.vue';

export default {
	name: 'b-input',
	components: {
		BIcon
	},
	props: {
		datePickerEvent: {
			type: Object,
			default: () => {}
		},
		inputClasses: {
			type: String,
			default:
				'font-size-big ink-base placeholder-ink-lighter-2 padding-normal border border-white-darker-4 border-focus-ink-lighter-1  bg-white-darker-2 bg-focus-white-darker-3'
		},
		inputErrorClasses: {
			type: String,
			default:
				'font-size-big red-base placeholder-red-lighter-2 padding-normal border border-red-lighter-2 border-focus-red-base  bg-red-lighter-4 bg-focus-red-lighter-3'
		},
		labelClasses: {
			type: String,
			default: 'font-size-big ink-base semi-bold margin-bottom-normal'
		},
		labelErrorClasses: {
			type: String,
			default: 'font-size-big ink-base semi-bold margin-bottom-normal'
		},
		name: {
			type: String,
			required: true
		},
		label: {
			type: String,
			default: ''
		},
		value: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: 'Digite aqui...'
		},
		maxlength: {
			type: String,
			default: ''
		},
		counter: {
			type: Boolean
		},
		error: {
			type: String,
			default: ''
		},
		type: {
			type: String,
			default: 'text',
			validator(val) {
				return val === 'text' || val === 'password' || val === 'number' || val === 'email';
			}
		},
		autosize: Boolean,
		disabled: Boolean,
		noAutocomplete: Boolean
	},
	computed: {
		inputClassesToApply() {
			if (this.error) return this.inputErrorClasses;
			else return this.inputClasses;
		},
		labelClassesToApply() {
			if (this.error) return this.labelErrorClasses;
			else return this.labelClasses;
		}
	},
	methods: {
		updateSize() {
			if (this.autosize) {
				this.$refs.input.style.width = '0px';
				var realWidth = this.$refs.input.scrollWidth;
				if (this.$refs.input.value === '') realWidth += this.$refs.input.placeholder.length * 10;
				this.$refs.input.style.width = realWidth + 2 + 'px';
			}
		}
	},
	mounted() {
		this.updateSize();
	},
	updated() {
		this.updateSize();
	}
};
</script>

<style scoped lang="scss">
@import '../css/_variables.scss';

label,
label * {
	box-sizing: border-box;
}
label {
	display: block;
}
.input-wrapper {
	position: relative;
	width: 100%;
	display: inline-block; //to counter margin merging
	&.autosize {
		width: auto;
		display: inline-block;
		input {
			width: auto;
		}
	}
}
input {
	border-radius: 5px;
	display: block;
	width: 100%;
	position: relative;
	z-index: 1;
	outline: none;
	&:disabled {
		color: map-get($colors, 'ink-lighter-2');
		cursor: not-allowed;
	}
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
span.icon-left {
	position: absolute;
	left: 1.3125rem;
	top: calc(0.8125rem + 0.05em);
	color: #8f8a9b;
	z-index: 2;
}
span.icon-right {
	position: absolute;
	top: calc(0.8125rem + 0.05em);
	right: 1.3125rem;
	color: #8f8a9b;
	z-index: 2;
}
input.has-icon-left {
	padding-left: calc(2.625rem + 0.8em);
}
input.has-icon-right {
	padding-right: calc(2.625rem + 0.8em);
}
span.input-error {
	display: block;
	text-align: left;
	line-height: 1.2;
}
label span.label {
	display: block;
	text-align: left;
}
</style>

<docs>
    BInput
    ```js
    <b-input label="Label comes here" />
    <b-input class="margin-top-normal" label="Autosize input" autosize />
    <b-input class="margin-top-normal" label="Autosize input" autosize disabled/>
    ```
</docs>
